// UserDataContext.js - Centralized user data management
import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { getUserData, calculateOfflineMining, claimMinedDoge } from '../services/appwrite';

const UserDataContext = createContext();

export const UserDataProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const pendingRequestRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const isInitializedRef = useRef(false);
  const hasCalculatedOfflineMiningRef = useRef(false);

  // Only log on mount in development mode (StrictMode now disabled to prevent duplicate operations)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !isInitializedRef.current) {
      console.log('🏗️ UserDataProvider: Initialized (StrictMode disabled to prevent duplicate operations)');
      isInitializedRef.current = true;
    }
    
    // Cleanup on unmount
    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('🧹 UserDataProvider: Unmounted');
      }
    };
  }, []);

  // Track app visibility to determine when user returns
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isInitialLoadRef.current) {
        // User returned to app after being away
        hasCalculatedOfflineMiningRef.current = false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Direct data fetcher without caching
  const fetchUserData = useCallback(async (userId, forceRefresh = false, calculateOffline = false) => {
    if (!userId) return null;

    // If there's already a pending request for this user, wait for it
    if (pendingRequestRef.current && pendingRequestRef.current.userId === userId.toString()) {
      return pendingRequestRef.current.promise;
    }

    // Create new request
    setIsLoading(true);
    const requestPromise = (async () => {
      try {
        let offlineResult = null;
        
        // Only calculate offline mining on first load or when explicitly requested
        if (calculateOffline && !hasCalculatedOfflineMiningRef.current) {
          offlineResult = await calculateOfflineMining(userId);
          hasCalculatedOfflineMiningRef.current = true;
          
          // After first load, mark as no longer initial
          if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
          }
        }
        
        // Get fresh user data
        const userData = await getUserData(userId);
        
        if (userData) {
          // Combine the result
          const combinedData = {
            ...userData,
            offlineMining: offlineResult
          };
          
          setUserData(combinedData);
          return combinedData;
        }
        
        return null;
      } catch (error) {
        console.error('Error fetching user data:', error);
        return null;
      } finally {
        setIsLoading(false);
        pendingRequestRef.current = null;
      }
    })();

    // Store pending request
    pendingRequestRef.current = {
      userId: userId.toString(),
      promise: requestPromise
    };

    return requestPromise;
  }, []); // No dependencies - all state accessed via refs

  // Claim function with immediate state update
  const claimDoge = useCallback(async (userId, minedAmount) => {
    if (!userId || !minedAmount || minedAmount < 100) {
      return { success: false, error: 'Invalid claim parameters' };
    }

    try {
      const result = await claimMinedDoge(userId, minedAmount);
      
      if (result.success) {
        // Immediately update state
        setUserData(prev => {
          if (prev) {
            return {
              ...prev,
              balance: result.newBalance,
              minedAmount: 0,
              lastClaimTime: result.claimTime
            };
          }
          return prev;
        });
      }
      
      return result;
    } catch (error) {
      console.error('Error claiming DOGE:', error);
      return { success: false, error: error.message };
    }
  }, []);

  // Force refresh function
  const refreshUserData = useCallback(async (userId) => {
    return fetchUserData(userId, true, false); // No offline calculation on manual refresh
  }, [fetchUserData]);

  // Initial data load with offline mining calculation
  const initialLoadUserData = useCallback(async (userId) => {
    // Prevent duplicate initial loads for the same user
    if (pendingRequestRef.current?.userId === userId.toString()) {
      return pendingRequestRef.current.promise;
    }
    
    return fetchUserData(userId, false, true);
  }, [fetchUserData]);

  // Standard getUserData function
  const getOptimizedUserData = useCallback(async (userId) => {
    return fetchUserData(userId, false, false);
  }, [fetchUserData]);

  // Update specific fields in state
  const updateCachedField = useCallback((userId, field, value) => {
    setUserData(prev => {
      if (prev) {
        return {
          ...prev,
          [field]: value
        };
      }
      return prev;
    });
  }, []);

  const value = useMemo(() => ({
    userData,
    isLoading,
    fetchUserData,
    getOptimizedUserData,
    initialLoadUserData,
    claimDoge,
    refreshUserData,
    getCachedUserData: () => userData, // Simple return current data
    updateCachedField,
    isCacheValid: () => false // Always refresh since no cache
  }), [
    userData,
    isLoading,
    fetchUserData,
    getOptimizedUserData,
    initialLoadUserData,
    claimDoge,
    refreshUserData,
    updateCachedField
  ]);

  return (
    <UserDataContext.Provider value={value}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};

export default UserDataContext;
